import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { MouseEvent } from 'react';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { UserTierBadge } from '@flipster/features/src/shared/user-tier/badge';
import { useUserTier } from '@flipster/features/src/shared/user-tier/state';
import { useZendesk } from '@flipster/features/src/shared/zendesk/use-zendesk';
import { Link } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button, StatusBadge } from '@flipster/react/design-system';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { snackbar } from '@flipster/react/design-system/snackbar';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import GiftIcon from '@flipster/react/icon/generated/GiftIcon';
import GlobalIcon from '@flipster/react/icon/generated/GlobalIcon';
import UserVipstatusBasicAccountColorIcon from '@flipster/react/icon/generated/UserVipstatusBasicAccountColorIcon';
import UserVipstatusVipAccountColorIcon from '@flipster/react/icon/generated/UserVipstatusVipAccountColorIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { getErrorMessage } from '@flipster/utils/error';
import segment, { SessionEvent } from '@flipster/utils/segment';
import { GNBEvent } from '@flipster/utils/segment/events/gnb';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';
import { RewardEvent } from '@flipster/utils/segment/events/reward';

import { LanguageModal } from '@web/app/_layout/language-modal';
import { useRedDot } from '@web/hooks/useRedDot';

import { NotificationCenter } from './notification/notification-center';
import AccountNav from './shared/AccountNav';
import { menuItemCssRaw, menuListCssRaw, visibleHiddenDesktopDownCssRaw } from './shared/css';
import { Dropdown } from './shared/dropdown';
import { useUpdateRedDotMutation } from './shared/useUpdateRedDotMutation';

const RewardHubButton = () => {
  const redDotQuery = useRedDot();
  const { mutateAsync: updateRedDot } = useUpdateRedDotMutation();

  const onClickRewardhubButton = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    const gnbAction = e.currentTarget.dataset.action;
    if (!gnbAction) throw new Error('data-action 은 필수값이니깐 넣어주세요');
    try {
      segment.track(RewardEvent.GnbRewardHubClick, {
        red_dot: redDotQuery.data?.gnb.includes('REWARD_HUB'),
      });
      await updateRedDot({ gnb: gnbAction });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  };

  return (
    <Link href='/reward-hub' data-action='REWARD_HUB' onClick={onClickRewardhubButton}>
      <div
        className={center({
          h: 40,
          w: 40,
          rounded: 4,
          color: 'content.default.level-4',
          bgColor: 'background.default.level-1',
        })}
      >
        <StatusBadge
          visible={Boolean(redDotQuery.data?.gnb.includes('REWARD_HUB'))}
          positionType='icon'
          css={{
            _after: {
              right: -2,
              top: -1,
            },
          }}
        >
          <GiftIcon size={20} />
        </StatusBadge>
      </div>
    </Link>
  );
};

const LanguageButton = () => {
  const overlay = useOverlay();

  return (
    <button
      type='button'
      className={css(afterLoginButtonCssRaw)}
      onClick={() => {
        segment.track(GNBEvent.GnbLanguageClick);
        overlay.open(<LanguageModal />);
      }}
    >
      <GlobalIcon size={20} />
    </button>
  );
};

const AccountDropdown = () => {
  const transNavigation = useTranslations('Navigation.r250401');
  const transCommon = useTranslations('Common.r250401');
  const { isKolAtom, logout } = useBunja(authBunja);
  const isKol = useAtomValue(isKolAtom);
  const { userTier, isVip } = useUserTier();
  const isSpotOnly = useCheckSpotOnly();
  const redDotQuery = useRedDot();
  const { zendeskLogout } = useZendesk();
  const { mutateAsync: updateRedDot } = useUpdateRedDotMutation();

  const onClickRedDot = async (e: MouseEvent<HTMLAnchorElement>) => {
    const gnbAction = e.currentTarget.dataset.action;
    if (!gnbAction) throw new Error('data-action 은 필수값이니깐 넣어주세요');
    try {
      await updateRedDot({ gnb: gnbAction });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  };

  return (
    <Dropdown
      position='right'
      trigger={
        <div className={css({ textStyle: 'Text/L/Regular', gap: 4 }, afterLoginButtonCssRaw)}>
          {isVip ? <UserVipstatusVipAccountColorIcon /> : <UserVipstatusBasicAccountColorIcon />}
          <span>{isVip ? `VIP ${userTier?.vipTier || 0}` : 'Basic'}</span>
          <CaretDownIcon size={12} className='dropdown-icon' />
        </div>
      }
      dropdown={
        <div className={stack({ minW: 210 })}>
          <AccountNav />
          {!isSpotOnly && (
            <Link
              href='/user/status'
              className={css({
                w: '100%',
                justifyContent: 'space-between',
                py: 8,
                borderBottom: '1px solid',
                borderBottomColor: 'border.default.level-1',
                color: 'content.default.level-4',
                _hover: { color: 'content.accent.info' },
              })}
              onClick={() =>
                segment.track(ProfileEvent.AccountInfoTypeClick, { account_info: 'vip_status', is_kol: isKol })
              }
            >
              <dl
                className={hstack({
                  justify: 'space-between',
                  py: 8,
                })}
              >
                <dt className={css({ textStyle: 'Text/M/Strong' })}>{transNavigation('Vip_status')}</dt>
                <dd className={hstack()}>
                  <UserTierBadge size='medium' status={userTier?.status} tier={userTier?.vipTier} />
                  <IconButton
                    hierarchy='quaternary'
                    size='small'
                    icon={<ChevronRightIcon color={token('colors.content.default.level-3')} />}
                  />
                </dd>
              </dl>
            </Link>
          )}
          <ul className={css(menuListCssRaw, { minW: 100, pt: 16, pb: 4, w: '100%' })}>
            {!isSpotOnly && (
              <li>
                <Link
                  href={isKol ? '/support/affiliate' : '/support/referral'}
                  className={css(menuItemCssRaw)}
                  data-action='AFFILIATE_DASHBOARD'
                  onClick={(e) => {
                    segment.track(ProfileEvent.AccountInfoTypeClick, { account_info: 'referral', is_kol: isKol });

                    if (isKol && redDotQuery.data?.gnb.includes('AFFILIATE_DASHBOARD')) {
                      onClickRedDot(e);
                    }
                  }}
                >
                  <StatusBadge
                    visible={isKol && redDotQuery.data?.gnb.includes('AFFILIATE_DASHBOARD')}
                    positionType='text'
                  >
                    {isKol ? transNavigation('Affiliate') : transNavigation('Referral')}
                  </StatusBadge>
                </Link>
              </li>
            )}
            <li>
              <button
                type='button'
                onClick={() => {
                  zendeskLogout();
                  segment.track(ProfileEvent.AccountInfoTypeClick, { account_info: 'logout', is_kol: isKol });
                  logout();
                }}
                className={css(menuItemCssRaw, { w: '100%' })}
              >
                {transCommon('Log_out')}
              </button>
            </li>
          </ul>
        </div>
      }
    />
  );
};

export const GnbAuthActions = () => {
  const transCommon = useTranslations('Common.r250401');
  const { isLoggedInAtom, getMeAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const { isLoading } = useAtomValue(getMeAtom);
  const isMounted = useMounted();
  const { isDesktop } = useBreakpoint();
  const isSpotOnly = useCheckSpotOnly();

  if (!isMounted || isLoading) return null;

  if (!isLoggedIn)
    return (
      <div className={css(actionLayoutRaw, visibleHiddenDesktopDownCssRaw)}>
        <LanguageButton />
        <Button
          hierarchy='secondary'
          color='neutral'
          size='medium'
          as='a'
          href='/login'
          label={transCommon('Log_in')}
          onClick={() => segment.track(SessionEvent.GnbLoginClick, { button_text: 'Login' })}
          css={buttonCssRaw}
        />
        <Button
          hierarchy='primary'
          color='accent'
          size='medium'
          as='a'
          href='/signup'
          label={transCommon('Sign_up')}
          onClick={() => segment.track(SessionEvent.GnbLoginClick, { button_text: 'Sign up' })}
          css={buttonCssRaw}
        />
      </div>
    );

  if (isDesktop)
    return (
      <div className={css(actionLayoutRaw)}>
        {!isSpotOnly && <RewardHubButton />}
        <NotificationCenter />
        <LanguageButton />
        <AccountDropdown />
      </div>
    );
};

const actionLayoutRaw = hstack.raw({ gap: 8 });
const buttonCssRaw = css.raw({ w: 96 });
const afterLoginButtonCssRaw = center.raw({
  h: 40,
  px: 12,
  rounded: 4,
  color: 'content.default.level-4',
  bgColor: 'background.default.level-1',
});
